import { call, put, select, takeEvery } from 'redux-saga/effects';
import { ACCOUNT_TYPES, APP_DATA_KEYS } from 'JS/config/consts';
import { appServices } from 'JS/shared/helpers/app-services/AppServices';
import { AUTH_SUCCESS } from 'JS/actionCreators/authActions';
import getAudioFadeTimings from 'JS/shared/helpers/getAudioFadeTimings';

import {
  DOWNLOAD_REQUEST,
  STOP_RENDER_STATUS_POLLING,
  downloadRequestSuccess,
  downloadRequestFailed,
  startRenderStatusPolling,
  getVideoDownloadUrlSuccess,
  getVideoDownloadUrlFailed,
  GET_VIDEO_DOWNLOAD_URL_SUCCESS,
  updateDownloadCount,
  updateDownloadCountFailed
} from '../actionCreators/downloadActions';
import { sendErrorToSentry } from '../logging';
import getScribeLengthMs from '../playback/lib/Playback/helpers/getScribeLengthMs';

import { getUserAccountType } from './selectors';

function* requestDownload(action) {
  try {
    const scribe = yield select(state => state.scribes.allScribes.find(scribe => scribe.id === action.scribeId));
    const DURATION = 2;
    const visibility = 'private';

    const scribeLength = getScribeLengthMs(scribe) / 1000;
    const numSegments = Math.ceil(scribeLength / DURATION);

    const segments = new Array(numSegments).fill(0).map((_, i) => ({
      projectId: action.scribeId,
      startTime: i * DURATION,
      duration: DURATION
    }));

    const assets = [];

    const { audioClips = [] } = scribe;

    for (const clip of audioClips) {
      const { assetId, volume = 1 } = clip;
      const fadeTimings = yield call(getAudioFadeTimings, clip, scribe);

      assets.push({
        id: assetId,
        type: 'audio',
        volume,
        ...fadeTimings
      });
    }

    const result = yield call(appServices.renderCloud, action.scribeId, visibility, segments, action.format, assets);
    const shortId = result.url.slice(result.url.lastIndexOf('/') + 1);
    yield put(startRenderStatusPolling(shortId, action.format));
    yield put(downloadRequestSuccess(numSegments));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(downloadRequestFailed(e));
  }
}

function* requestVideoDownloadUrl(action) {
  try {
    const result = yield call(appServices.getVideoDownloadUrl, action.shortId, action.format);
    yield put(getVideoDownloadUrlSuccess(result));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(getVideoDownloadUrlFailed(e));
  }
}

function* logSuccessfulDownloadCount() {
  try {
    const userAccountType = yield select(getUserAccountType);
    if (userAccountType === ACCOUNT_TYPES.FREE) {
      const currentDownloadCount = yield select(state => state.download?.freeDownloadCount || 0);
      const { freeDownloadCount } = yield appServices.setAppData(APP_DATA_KEYS.FREE_DOWNLOAD_COUNT, {
        freeDownloadCount: currentDownloadCount + 1
      });
      yield put(updateDownloadCount(freeDownloadCount));
    }
  } catch (error) {
    yield put(updateDownloadCountFailed());
  }
}

function* fetchSuccessfulDownloadCount() {
  try {
    const userAccountType = yield select(getUserAccountType);
    if (userAccountType === ACCOUNT_TYPES.FREE) {
      const { freeDownloadCount } = yield appServices.getAppData(APP_DATA_KEYS.FREE_DOWNLOAD_COUNT);
      yield put(updateDownloadCount(freeDownloadCount));
    }
  } catch (error) {
    yield put(updateDownloadCountFailed());
  }
}

function* downloadSagas() {
  yield takeEvery(DOWNLOAD_REQUEST, requestDownload);
  yield takeEvery(STOP_RENDER_STATUS_POLLING, requestVideoDownloadUrl);
  yield takeEvery(GET_VIDEO_DOWNLOAD_URL_SUCCESS, logSuccessfulDownloadCount);
  yield takeEvery(AUTH_SUCCESS, fetchSuccessfulDownloadCount);
}

export default downloadSagas;
